import { jsx as _jsx } from "react/jsx-runtime";
// src/plugins/builder/templates/competitor-mention-flag/generator.tsx
// UI-only: filters reviews by substring-match against a list of competitor names.
import meta from './template.json';
import { manifestFor, header, readme, assemble, safeString } from '../_helpers';
import { SimplePage } from '../../runtime/SimplePage';
function matches(text, names, ci) {
    if (!text)
        return false;
    const hay = ci ? text.toLowerCase() : text;
    return names.some(n => {
        const needle = ci ? n.toLowerCase() : n;
        return needle && hay.includes(needle);
    });
}
const RuntimeUi = (ctx) => {
    const cfg = ctx.config;
    const names = (cfg.competitors ?? '').split(',').map(s => s.trim()).filter(Boolean);
    return (_jsx(SimplePage, { title: ctx.instanceName, subtitle: names.length ? `Watching for: ${names.join(', ')}` : 'No competitors configured yet.', source: "reviews", filterReview: (r) => matches(r.body, names, cfg.caseInsensitive), emptyTitle: "Nothing flagged yet", emptyBody: "When a review mentions one of the names you set, it'll show up here." }));
};
const generate = (params) => {
    const cfg = params.config;
    const manifest = manifestFor({
        instanceId: params.instanceId, name: params.instanceName,
        description: `Flags reviews that mention: ${cfg.competitors || '(none set)'}`,
        category: 'utility', icon: 'Flag',
        permissions: ['read:reviews', 'ui:toast'],
        entry: 'ui',
        config: meta.configSchema,
        defaultConfig: { competitors: cfg.competitors, caseInsensitive: cfg.caseInsensitive },
    });
    const competitorsJson = JSON.stringify((cfg.competitors ?? '').split(',').map(s => s.trim()).filter(Boolean));
    const pluginTsx = header({ name: params.instanceName, templateId: meta.id, instanceId: params.instanceId }) +
        `import { useReviews } from '../sdk';
import { EmptyState } from '../../ui/EmptyState';

const NAMES: string[] = ${competitorsJson};
const CI = ${Boolean(cfg.caseInsensitive)};

function mentions(text: string | null): boolean {
  if (!text) return false;
  const hay = CI ? text.toLowerCase() : text;
  return NAMES.some(n => hay.includes(CI ? n.toLowerCase() : n));
}

export default function ${cls(params.instanceId)}() {
  const { reviews } = useReviews({ limit: 100 });
  const rows = (reviews ?? []).filter(r => mentions(r.body));
  return (
    <main id="main" className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-2xl font-semibold">${esc(params.instanceName)}</h1>
      <p className="mt-1 text-sm text-slate-500">Watching for: ${esc(cfg.competitors)}</p>
      {rows.length === 0
        ? <EmptyState title="Nothing flagged yet" body="Competitor mentions will show up here." />
        : <ul className="mt-6 space-y-3">{rows.map(r => (<li key={r.id} className="rounded-2xl bg-white p-4 ring-1 ring-slate-200"><p className="text-sm font-medium">{r.author_name ?? 'Anonymous'} · {r.rating ?? '?'}★</p><p className="mt-1 text-sm text-slate-600">{r.body}</p></li>))}</ul>}
    </main>
  );
}
`;
    return assemble({ manifest, pluginTsx, hooksTs: null,
        readmeMd: readme({ name: params.instanceName, templateId: meta.id,
            description: `Flags reviews that mention competitor names.`,
            configSummary: [['competitors', cfg.competitors], ['caseInsensitive', String(cfg.caseInsensitive)]],
        }),
    });
};
function esc(s) { return safeString(String(s)); }
function cls(id) { return 'P_' + id.replace(/[^a-zA-Z0-9]+/g, '_'); }
const template = {
    id: meta.id, name: meta.name, tagline: meta.tagline, icon: meta.icon,
    category: meta.category,
    permissions: meta.permissions,
    permissionSummary: meta.permissionSummary,
    entry: meta.entry,
    configSchema: meta.configSchema,
    defaultConfig: meta.defaultConfig,
    suggestName: (cfg) => {
        const first = cfg.competitors.split(',')[0]?.trim();
        return first ? `Watching for "${first}"` : 'Competitor watch';
    },
    describe: (cfg) => `Flag any review that mentions: ${cfg.competitors || '(you\'ll set this up)'}`,
    generate, runtimeUi: RuntimeUi,
    clickCount: meta.clickCount,
};
export default template;
